import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import Promise from 'bluebird';
import _ from 'underscore';
import $ from 'jquery';
import { Selector } from './selector';
import { Pagination } from './pagination';
import { Search } from './search';
import { Option } from './option';
import template from './dropdown-search-selector-template.pug';

const { updateView, ensureChildView } = helpers;

export class DropdownSearchSelector extends DeterministicView {
  initialize({
    selectOption,
    placeholder,
    dataSource,
    pageSize = 10,
    i18n,
    customOptions = [],
    forceEnableSearch = false,
    invokeSearchOnChange = false, // invoke search on change
    nodataPlaceholder = '',
    isDisabled = false,
  }) {
    this.model.set({ selectOption: { id: '', value: '' } });
    this.model.set({ isDisabled });
    this.dataSource = dataSource;
    this.pageSize = pageSize;
    this.placeholder = placeholder;
    this.i18n = i18n;
    this.customOptions = customOptions;
    this.forceEnableSearch = forceEnableSearch;
    this.invokeSearchOnChange = invokeSearchOnChange;
    this.nodataPlaceholder = nodataPlaceholder;
    this.searchResultsId = _.uniqueId('search-results-');

    $(window).click((e) => {
      const clickedEl = $(e.target);
      const dropDown = this.$('.dropdown-search-selector__dropdown');

      if (clickedEl.closest(dropDown).length === 0 && dropDown.hasClass('open')) {
        this.toggle();
      }
    });
    this.viewLoadAsync(selectOption || { id: '', value: '' });
  }

  viewLoadAsync(selectOption = _.noop()) {
    return this.fetchData(this.dataSource, {
      pageNumber: 1,
      pageSize: this.pageSize,
    }).then((results) => {
      let { options } = results;

      if (results.totalCount > this.pageSize) {
        // enable search and pagination
        const { totalCount } = results;

        this.initialPaginationView(this.i18n, totalCount);
        this.initialSearchView(this.placeholder);
        options = options.slice(0, this.pageSize);
      } else {
        if (this.getChild('pagination-container')) {
          this.detachChild('pagination-container').remove();
        }

        if (this.forceEnableSearch) {
          this.initialSearchView(this.placeholder);
        } else if (this.getChild('search-container')) {
          this.detachChild('search-container').remove();
        }
      }

      // clean the search term
      if (this.getChild('search-container')) {
        this.$('#srch-term').val('');
        this.searchString = '';
      }
      if (selectOption) {
        this.setState({ selectOption });
      }

      if (this.customOptions) {
        _.each(this.customOptions, (option) => {
          option.id = option.id || option.value; // eslint-disable-line
          this.initialCustomOptionView(option, selectOption);
        });
      }
      this.setOptionView(options, selectOption);
      this.render();
    });
  }

  get events() {
    return {
      'click .dropdown-toggle': () => {
        this.toggle();
      },
    };
  }

  fetchData(dataSource, params) {
    return new Promise((resolve) => {
      if (_.isFunction(dataSource)) {
        return resolve(dataSource(params));
      }
      return resolve({
        options: [],
        totalCount: 0,
      });
    });
  }

  onOptionSelected(selectOption) {
    updateView(this, {
      selectOption,
    });
    this.trigger('option-selected', selectOption);
    this.selectedId = selectOption.id;
    this.toggle();
  }

  toggle() {
    this.$('.dropdown-toggle').parent().toggleClass('open');
  }

  get template() {
    return template;
  }

  onSearchChanged(searchString) {
    if (this.invokeSearchOnChange) {
      this.onSearchInvoked(searchString);
    }
  }

  onSearchInvoked(searchString) {
    if (this.searchString !== searchString) {
      this.searchString = searchString;
      this.fetchData(this.dataSource, {
        searchString,
        pageNumber: 1,
        pageSize: this.pageSize,
      }).then((results) => {
        const { totalCount } = results;

        if (this.getChild('pagination-container')) {
          this.getChild('pagination-container').setState({
            currentPage: 1,
            totalCount,
          });
        }
        this.getChild('selector-container').setState({
          options: results.options,
        });
      });
    }
  }

  onPageChanged(currentPage) {
    this.fetchData(this.dataSource, {
      searchString: this.searchString,
      pageNumber: currentPage,
      pageSize: this.pageSize,
    }).then((results) => {
      const { totalCount } = results;

      if (this.getChild('pagination-container')) {
        this.getChild('pagination-container').setState({
          currentPage,
          totalCount,
        });
      }
      this.getChild('selector-container').setState({
        options: results.options,
      });
    });
  }

  initialSearchView(placeholder) {
    ensureChildView(this, 'search-container', Search, {
      placeholder,
      searchResultsId: this.searchResultsId,
    }, {
      searchInvoked: 'onSearchInvoked',
      searchChanged: 'onSearchChanged',
    });
  }

  initialPaginationView(i18n, totalCount) {
    ensureChildView(this, 'pagination-container', Pagination, { i18n, pageSize: this.pageSize, totalCount }, {
      pageChanged: 'onPageChanged',
    }).setState({
      totalCount,
      currentPage: 1,
    });
  }

  setOptionView(options, selectOption) {
    ensureChildView(this, 'selector-container', Selector, { options, selectOption, nodataPlaceholder: this.nodataPlaceholder }, {
      optionSelected: 'onOptionSelected',
    }).setState({ options, selectOption });
  }

  initialCustomOptionView(customOption, selectOption) {
    if (!customOption.onOptionSelected) {
      customOption.onOptionSelected = ({ id }) => { // eslint-disable-line
        const option = _.findWhere(this.customOptions, { id });

        this.onOptionSelected(option);
      };
    }
    ensureChildView(this, `${customOption.id}-container`, Option, {}, {
      optionSelected: ({ id }) => customOption.onOptionSelected({ id }),
    }).setState({
      id: customOption.id,
      value: customOption.value,
      isSelected: selectOption.id === customOption.id,
    });
  }
}
